import NavigationUtil from 'backbone/utils/NavigationUtil';
import { PhasedTrainingPlanSubTypes } from 'constants/TrainingPlansConstants';
import moment from 'moment';
import DateFormatter from '../../../backbone/utils/date/DateFormatter';
import { getFirstDayOfWeek, getFirstDayOfWeekMoment } from '../../../utils/DateUtils';
import i18next from 'i18next';

export const NUMBER_OF_WEEKS_TO_CHECK_USER_ACTIVITIES = 4;
export const PHASED_TRAINING_PLAN_SETUP_PAGE_URL_PATH_NAME = 'training-plans/setup/';
export const FINAL_STEP = 7;
export const MINIMUM_DAYS_AVAILABILITY = 4;
export const ALL_POSSIBLE_TRAINING_PLAN_DAYS = [1, 2, 3, 4, 5, 6, 7];
export const ALL_POSSIBLE_TRAINING_PLAN_DAYS_WEEK_STARTS_SUNDAY = [7, 1, 2, 3, 4, 5, 6];
export const ALL_POSSIBLE_TRAINING_PLAN_DAYS_WEEK_STARTS_SATURDAY = [6, 7, 1, 2, 3, 4, 5];
export const MINIMUM_WEEKS_TILL_RACE = 6;
export const MAXIMUM_WEEKS_TILL_RACE = 30;
export const CURRENT_TRAINING_VALUES = [
    { value: '1', label: '0-4' },
    { value: '2', label: '5-9' },
    { value: '3', label: '10+' }
];

/**
 * all the possible training days ordered by the user preferred week start date
 * @param viewerUserPreferences - preference needed to order based on first day of week set by the user
 * @returns {number[]} - return an array of numbers representing week days
 */
export const allTrainingDaysOrdered = (viewerUserPreferences) => {
    //dayId === 3 - week starts monday - this is the default
    let daysOrderedArray = [...ALL_POSSIBLE_TRAINING_PLAN_DAYS];

    if (viewerUserPreferences && viewerUserPreferences.firstDayOfWeek && viewerUserPreferences.firstDayOfWeek.dayId) {
        if (viewerUserPreferences.firstDayOfWeek.dayId === 2) {
            //starts on sunday
            daysOrderedArray = [...ALL_POSSIBLE_TRAINING_PLAN_DAYS_WEEK_STARTS_SUNDAY];
        } else if (viewerUserPreferences.firstDayOfWeek.dayId === 1) {
            //starts on saturday
            daysOrderedArray = [...ALL_POSSIBLE_TRAINING_PLAN_DAYS_WEEK_STARTS_SATURDAY];
        }
    }

    return daysOrderedArray;
};

export const navigateThroughSteps = (subType, step, customSetStep) => {
    let nextStepNumber;

    if (customSetStep && !isNaN(customSetStep)) {
        nextStepNumber = parseInt(customSetStep);
    } else {
        nextStepNumber = parseInt(step) + 1;
    }

    NavigationUtil.navigate(`/training-plans/setup/${subType}/${nextStepNumber}`);
};

export const canNavigateUpToStepNumber = (obj) => {
    if (typeof obj !== 'object') {
        return 1;
    }
    if (obj.hasPowerMeter || obj.hasHRMonitor) {
        if (obj.currentTrainingLevel) {
            if (obj.trainingPlanLevel) {
                if (obj.daysAvailable && obj.daysAvailable.length) {
                    if (obj.longRideDay) {
                        if (obj.eventDate) {
                            //if race date is set user can navigate to step 7
                            return 7;
                        }
                        //if long ride day is set user can navigate to step 6
                        return 6;
                    }
                    //if availability days are set user can navigate to step 5
                    return 5;
                }
                //if hours per week is set user can navigate to step 4
                return 4;
            }
            //if hours per week is set user can navigate to step 3
            return 3;
        }
        //if has only accessories set user can navigate to step 2
        return 2;
    }
    //by default the user should be allowed to go to the first step
    return 1;
};

export const hasMinimumNumberOfDaysSelected = (daysAvailableLength, trainingPlanLevelData) => {
    return daysAvailableLength >= trainingPlanLevelData.workoutsPerWeekMin - 1;
};

export const getNumberOfWorkoutsPerWeekRange = (itemObject) => {
    return itemObject.workoutsPerWeekMax !== itemObject.workoutsPerWeekMin
        ? `${itemObject.workoutsPerWeekMin} - ${itemObject.workoutsPerWeekMax}`
        : itemObject.workoutsPerWeekMin;
};

/**
 * check to see if the set training plan days are less than the minimum availability and then if they are consecutive
 * @param trainingPlanSetupData - array of numbers representing week days
 * @returns {boolean|boolean}
 */
export const areTrainingDaysConsecutive = (trainingPlanSetupData) => {
    const daysAvailable = trainingPlanSetupData.daysAvailable;

    return daysAvailable.length < MINIMUM_DAYS_AVAILABILITY && hasThreeConsecutiveDates(daysAvailable);
};

/**
 * checks to see if there are 3 or more consecutive numbers in an array - used to see if the training days are consecutives
 * also checks if is sunday - monday - tuesday or saturday - sunday - monday - not just strictly consecutive numbers
 * @param daysAvailable array of selected workout dates
 * @returns {boolean}
 */
const hasThreeConsecutiveDates = (daysAvailable) => {
    let allAvailableDays = [...daysAvailable].sort();
    let theyAreConsecutive = 0;
    let maxFound = 0;
    let numbersOfIterations = ALL_POSSIBLE_TRAINING_PLAN_DAYS.length + 3;

    for (let i = 0; i < numbersOfIterations; i++) {
        if (
            allAvailableDays.indexOf(ALL_POSSIBLE_TRAINING_PLAN_DAYS[i % ALL_POSSIBLE_TRAINING_PLAN_DAYS.length]) >= 0
        ) {
            theyAreConsecutive++;
        } else {
            if (theyAreConsecutive > maxFound) {
                maxFound = theyAreConsecutive;
            }
            theyAreConsecutive = 0;
        }
    }

    return maxFound >= 3;
};

export const isPhasedTrainingPlan = (trainingPlanObject) => {
    return trainingPlanObject && !!trainingPlanObject.peakWeeks;
};

export const getSelectedTrainingPlanHours = (currentTrainingPlan, trainingPlanLevel) => {
    let selectedTrainingLevel = currentTrainingPlan.levels.filter((item) => {
        return item.levelKey === trainingPlanLevel;
    });

    if (selectedTrainingLevel && selectedTrainingLevel[0] && selectedTrainingLevel[0].avgWorkoutHoursPerWeek) {
        return selectedTrainingLevel[0].avgWorkoutHoursPerWeek;
    }

    return 0;
};

export const shouldShowRecommendWorkoutModal = (trainingPlanSetupData) => {
    let firstOfCurrentTrainingValue = CURRENT_TRAINING_VALUES[0].value;

    return (
        firstOfCurrentTrainingValue === trainingPlanSetupData.currentTrainingLevel &&
        trainingPlanSetupData.trainingPlanLevel === 'Advanced'
    );
};

export const getPreselectedValueBasedOfCurrentWorkouts = (userCurrentTrainingData) => {
    if (userCurrentTrainingData) {
        let addedSum = 0;

        userCurrentTrainingData.forEach((activity) => {
            if (
                activity.stats &&
                activity.stats.all &&
                activity.stats.all.duration &&
                !isNaN(activity.stats.all.duration.sum)
            ) {
                addedSum += activity.stats.all.duration.sum;
            }
        });

        //average of hours per week, hours from milliseconds based on the numbers of weeks requested
        addedSum = addedSum / (NUMBER_OF_WEEKS_TO_CHECK_USER_ACTIVITIES * 60 * 60 * 1000);

        if (addedSum > 10) {
            //if over 10 hours
            return CURRENT_TRAINING_VALUES[2].value;
        } else if (addedSum > 5) {
            //if over 5 hours
            return CURRENT_TRAINING_VALUES[1].value;
        }
        //value if hours pe week is under 10 or 5 hours
        return CURRENT_TRAINING_VALUES[0].value;
    }

    //if no data available return first value
    return CURRENT_TRAINING_VALUES[0].value;
};

export const getNextWorkout = (phasedTrainingPlanData) => {
    const todayDate = new moment().format(DateFormatter.LINK_FORMAT);

    if (phasedTrainingPlanData && phasedTrainingPlanData.taskList) {
        return phasedTrainingPlanData.taskList.find((item) => {
            if (item.taskWorkout) {
                let isTodayAndDoesNotHaveActivityId =
                    item.calendarDate === todayDate && !item.taskWorkout.associatedActivityId;
                let isCurrentWorkoutDateTodayOrAfterToday =
                    isTodayAndDoesNotHaveActivityId || item.calendarDate > todayDate;

                return isCurrentWorkoutDateTodayOrAfterToday ? item : null;
            }
        });
    }

    return null;
};

export const separateWorkoutsByWeeks = (trainingPlan) => {
    let allWeeksAtIndex = [];

    if (trainingPlan && trainingPlan.taskList) {
        trainingPlan.taskList.forEach((item) => {
            if (!allWeeksAtIndex[item.weekId]) {
                allWeeksAtIndex[item.weekId] = [];
            }
            /**check if same date was already added because it seems that we can receive the same date multiple times from the api
             *
             */
            // let findDuplicate = allWeeksAtIndex[item.weekId].find((workout) => {
            //     return workout.calendarDate === item.calendarDate
            // });
            // if (findDuplicate) {
            //     console.log(item);
            // } else {
            allWeeksAtIndex[item.weekId].push(item);
            // }
        });
    }

    return allWeeksAtIndex;
};

export const separateWorkoutsByUserWeeks = (workoutsData, viewerUserPreferences) => {
    let allWeeksAtIndex = [];

    if (workoutsData && workoutsData.length) {
        let workouts = [...workoutsData];
        let mergeWorkoutDuplicatedDates = mergeDuplicateWorkouts(workouts);
        let parsedFirstDayOfWeek = getFirstDayOfWeek(viewerUserPreferences);
        let isNewWeekStartDate = (item) => {
            let getFirstDayOfWeek = getFirstDayOfWeekMoment(parsedFirstDayOfWeek, item.calendarDate).format(
                DateFormatter.LINK_FORMAT
            );
            return getFirstDayOfWeek === item.calendarDate;
        };

        let weekNumber = 0;
        allWeeksAtIndex[weekNumber] = [];

        if (mergeWorkoutDuplicatedDates) {
            mergeWorkoutDuplicatedDates.forEach((item) => {
                //if current day is a start of week and the current week array is not empty
                if (isNewWeekStartDate(item) && allWeeksAtIndex[weekNumber].length) {
                    weekNumber++;
                    allWeeksAtIndex[weekNumber] = [];
                }
                allWeeksAtIndex[weekNumber].push(item);
            });
        }

        allWeeksAtIndex = addMissingDaysToWorkoutWeeks(allWeeksAtIndex, parsedFirstDayOfWeek);
    }

    return allWeeksAtIndex;
};

const getRestDateObject = (currentDayOfWeek, dayOfWeek) => {
    return {
        calendarDate: currentDayOfWeek,
        taskNote: { calendarDate: currentDayOfWeek },
        taskWorkout: null,
        isForCompleteWeek: true,
        dayOfWeekId: dayOfWeek
    };
};

const mergeDuplicateWorkouts = (receivedWorkouts) => {
    let mergedArray = [];
    let workouts = [...receivedWorkouts];

    workouts.forEach((item) => {
        let indexOfFoundItemInMergedArray = null;
        let findDuplicate = mergedArray.find((workout, index) => {
            indexOfFoundItemInMergedArray = index;
            return workout.calendarDate === item.calendarDate;
        });
        if (findDuplicate) {
            let newObject = { ...findDuplicate };
            //make sure that the workout is added to the workout item in the list
            //we dont need the task notes, so we dont add extra notes if more are available - if they should be needed in the future they can be merged here
            if (!newObject.taskWorkout && item.taskWorkout) {
                newObject.taskWorkout = { ...item.taskWorkout };
            } else if (newObject.taskWorkout && item.taskWorkout) {
                //make sure this is not a reference
                newObject.taskWorkout = { ...findDuplicate.taskWorkout };
                //if multiple workouts per day
                if (newObject.workoutsList) {
                    //when adding the workouts in an existing array just add the missing one
                    let workoutToAdd = {
                        ...item.taskWorkout,
                        workoutOrder: item.workoutOrder,
                        trainingPlanId: item.trainingPlanId
                    };
                    newObject.workoutsList.push(workoutToAdd);
                } else {
                    //when first adding the workouts in an array add both
                    let firstWorkoutToAdd = {
                        ...newObject.taskWorkout,
                        workoutOrder: newObject.workoutOrder,
                        trainingPlanId: newObject.trainingPlanId
                    };
                    let secondWorkoutToAdd = {
                        ...item.taskWorkout,
                        workoutOrder: item.workoutOrder,
                        trainingPlanId: item.trainingPlanId
                    };
                    newObject.workoutsList = [firstWorkoutToAdd, secondWorkoutToAdd];
                }
                if (newObject.workoutsList && newObject.workoutsList.length) {
                    newObject.workoutsList.sort((a, b) => (a.workoutOrder > b.workoutOrder ? 1 : -1));
                }
            }
            mergedArray.splice(indexOfFoundItemInMergedArray, 1, newObject);
        } else {
            mergedArray.push({ ...item });
        }
    });

    return mergedArray;
};

const addMissingDaysToWorkoutWeeks = (allWorkoutWeeks, parsedFirstDayOfWeek) => {
    allWorkoutWeeks.forEach((workoutWeek, indexOfWeek) => {
        if (workoutWeek.length < 7) {
            let createdFullWeek = [];
            let getFirstDayOfWeek = getFirstDayOfWeekMoment(parsedFirstDayOfWeek, workoutWeek[0].calendarDate).format(
                DateFormatter.LINK_FORMAT
            );
            let currentDayOfWeek = null;
            for (let i = 0; i < 7; i++) {
                currentDayOfWeek = new moment(getFirstDayOfWeek, DateFormatter.LINK_FORMAT)
                    .add(i, 'days')
                    .format(DateFormatter.LINK_FORMAT);
                let workoutDataExists = workoutWeek.find((workoutData) => {
                    return workoutData.calendarDate === currentDayOfWeek;
                });
                if (workoutDataExists) {
                    //if we have workout data for that day set that
                    createdFullWeek.push(workoutDataExists);
                } else {
                    let dayOfWeek = new moment(getFirstDayOfWeek, DateFormatter.LINK_FORMAT).add(i, 'days').day();
                    if (dayOfWeek === 0) dayOfWeek = 7; // 0 is sunday but we need it as id 7
                    //if we dont have workout data for that day set mock rest date
                    createdFullWeek.push({ ...getRestDateObject(currentDayOfWeek, dayOfWeek) });
                }
            }
            allWorkoutWeeks[indexOfWeek] = createdFullWeek;
        }
    });

    return allWorkoutWeeks;
};

export const getCurrentDateTrainingInfo = (workoutsByWeek) => {
    let today = new moment().format(DateFormatter.LINK_FORMAT);
    let indexToReturn = 0;

    if (workoutsByWeek && workoutsByWeek.length) {
        workoutsByWeek.forEach((workoutWeek, weekIndex) => {
            workoutWeek.find((workout) => {
                let workoutDate = workout.calendarDate;
                if (workoutDate && workoutDate.indexOf(today) !== -1) {
                    indexToReturn = weekIndex;
                }
            });
        });
    }

    return indexToReturn;
};

export const setWorkoutPhaseStartDates = (phasedTrainingPlanData) => {
    let workoutPhases = {
        basePhase: null,
        buildPhase: null,
        peakPhase: null,
        totalDays: 0,
        recoveryWeeks: []
    };
    let trainingPlans = phasedTrainingPlanData.taskList;
    let trainingPlansReversed = [...phasedTrainingPlanData.taskList].reverse();

    //base week and build week are optional based of tp level and duration
    let firstBaseWeek = phasedTrainingPlanData.baseWeeks ? 1 : null;
    let firstBuildWeek = phasedTrainingPlanData.baseWeeks
        ? phasedTrainingPlanData.baseWeeks + 1
        : phasedTrainingPlanData.buildWeeks
        ? 1
        : null;
    let firstPeakWeek = phasedTrainingPlanData.peakWeeks
        ? phasedTrainingPlanData.durationInWeeks - phasedTrainingPlanData.peakWeeks + 1
        : null;
    let lastBaseWeek = firstBaseWeek && firstBuildWeek ? (firstBuildWeek - 1 > 1 ? firstBuildWeek - 1 : 1) : null;
    let lastBuildWeek = firstBuildWeek && firstPeakWeek ? (firstPeakWeek - 1 > 1 ? firstPeakWeek - 1 : 1) : null;
    let recoveryWeeks =
        phasedTrainingPlanData.weekTypes && phasedTrainingPlanData.weekTypes.RECOVERY
            ? phasedTrainingPlanData.weekTypes.RECOVERY
            : null;

    if (firstBaseWeek) {
        let firstTpFromBaseWeek = trainingPlans.find((trainingPlanData) => trainingPlanData.weekId === firstBaseWeek);
        workoutPhases.basePhase = { basePhaseStart: firstTpFromBaseWeek.calendarDate };
        if (lastBaseWeek) {
            let lastTpFromBaseWeek = trainingPlansReversed.find(
                (trainingPlanData) => trainingPlanData.weekId === lastBaseWeek
            );
            workoutPhases.basePhase.basePhaseEnd = lastTpFromBaseWeek.calendarDate;
            //calculate length of phase in days adding 1 to include start date
            workoutPhases.basePhase.phaseLengthInDays =
                new moment(lastTpFromBaseWeek.calendarDate).diff(workoutPhases.basePhase.basePhaseStart, 'days') + 1;
            workoutPhases.totalDays = workoutPhases.totalDays += workoutPhases.basePhase.phaseLengthInDays;
            workoutPhases.basePhase.weeksAndDays = getNumberOfWeeksAndDays(workoutPhases.basePhase.phaseLengthInDays);
        }
    }
    if (firstBuildWeek) {
        let firstTpFromBuildWeek = trainingPlans.find((trainingPlanData) => trainingPlanData.weekId === firstBuildWeek);
        workoutPhases.buildPhase = { buildPhaseStart: firstTpFromBuildWeek.calendarDate };
        if (lastBuildWeek) {
            let lastTpFromBuildWeek = trainingPlansReversed.find(
                (trainingPlanData) => trainingPlanData.weekId === lastBuildWeek
            );
            workoutPhases.buildPhase.buildPhaseEnd = lastTpFromBuildWeek.calendarDate;
            //calculate length of phase in days adding 1 to include start date
            workoutPhases.buildPhase.phaseLengthInDays =
                new moment(lastTpFromBuildWeek.calendarDate).diff(workoutPhases.buildPhase.buildPhaseStart, 'days') + 1;
            workoutPhases.totalDays = workoutPhases.totalDays += workoutPhases.buildPhase.phaseLengthInDays;
            workoutPhases.buildPhase.weeksAndDays = getNumberOfWeeksAndDays(workoutPhases.buildPhase.phaseLengthInDays);
        }
    }
    if (firstPeakWeek) {
        let firstTpFromPeakWeek = trainingPlans.find((trainingPlanData) => trainingPlanData.weekId === firstPeakWeek);
        //peak phase ends on tp end
        let peakPhaseEndDate = new moment(phasedTrainingPlanData.endDate).format(DateFormatter.LINK_FORMAT);
        workoutPhases.peakPhase = { peakPhaseStart: firstTpFromPeakWeek.calendarDate, peakPhaseEnd: peakPhaseEndDate };
        //calculate length of phase in days adding 1 to include start date
        workoutPhases.peakPhase.phaseLengthInDays =
            new moment(peakPhaseEndDate).diff(firstTpFromPeakWeek.calendarDate, 'days') + 1;
        workoutPhases.totalDays = workoutPhases.totalDays += workoutPhases.peakPhase.phaseLengthInDays;
        workoutPhases.peakPhase.weeksAndDays = getNumberOfWeeksAndDays(workoutPhases.peakPhase.phaseLengthInDays);
    }
    if (recoveryWeeks && recoveryWeeks.length) {
        recoveryWeeks.forEach((recoveryWeek) => {
            let firstTpFromRecoveryWeek = trainingPlans.find(
                (trainingPlanData) => trainingPlanData.weekId === recoveryWeek
            );
            workoutPhases.recoveryWeeks.push(firstTpFromRecoveryWeek.calendarDate);
        });
    }

    return workoutPhases;
};

const getNumberOfWeeksAndDays = (number) => {
    let weeksAndDays = {
        weeks: 0,
        days: 0
    };

    if (!isNaN(number)) {
        weeksAndDays.weeks = Math.floor(number / 7);
        weeksAndDays.days = number % 7;
    }
    return weeksAndDays;
};

export const getCurrentTrainingPlanSetupObject = (phasedTrainingPlans, subType) => {
    if (phasedTrainingPlans && phasedTrainingPlans.length) {
        let filteredTrainingPlan = phasedTrainingPlans.filter((item) => {
            return item.trainingSubType.subTypeKey === subType;
        });

        return filteredTrainingPlan[0];
    }
    return false;
};

export const getDaysAndWeeksTranslationForPhase = (workoutPhasesWeeksAndDates) => {
    if (workoutPhasesWeeksAndDates) {
        let numberOfAvailableWeeks = workoutPhasesWeeksAndDates.weeks;
        let numberOfAvailableDays = workoutPhasesWeeksAndDates.days;

        let numberOfWeeks = () => {
            if (numberOfAvailableWeeks) {
                if (numberOfAvailableWeeks === 1) {
                    return ` • ${i18next.t(`training_plans:one_week_in_phase`, { 0: numberOfAvailableWeeks })}`;
                }
                return ` • ${i18next.t(`training_plans:training_plans_duration_weeks`, { 0: numberOfAvailableWeeks })}`;
            }
            return '';
        };

        let numberOfDays = () => {
            let separator = numberOfAvailableWeeks ? '&' : '•';
            if (numberOfAvailableDays) {
                if (numberOfAvailableDays === 1) {
                    return ` ${separator} ${i18next.t(`training_plans:one_day_in_phase`, {
                        0: numberOfAvailableDays
                    })}`;
                }
                return ` ${separator} ${i18next.t(`training_plans:number_of_days`, { 0: numberOfAvailableDays })}`;
            }
            return '';
        };

        return numberOfWeeks() + numberOfDays();
    }

    return null;
};

export const getCurrentDayInWorkouts = (workouts) => {
    let foundIndex = 1;
    if (workouts && workouts.length) {
        let todayDate = new moment().format(DateFormatter.LINK_FORMAT);
        workouts.find((item, index) => {
            if (item.calendarDate === todayDate) {
                //is a day number not an index so add 1
                foundIndex = index + 1;
            }
        });
    }
    return foundIndex;
};

export const getCurrentWeek = (workoutWeeks) => {
    let foundWeekAtIndex = 1;
    if (workoutWeeks && workoutWeeks.length) {
        let todayDate = new moment().format(DateFormatter.LINK_FORMAT);

        workoutWeeks.map((singleWeek, indexOfWeek) => {
            singleWeek.find((item) => {
                if (item.calendarDate === todayDate) {
                    //is a day number not an index so add 1
                    foundWeekAtIndex = indexOfWeek + 1;
                }
            });
        });
    }
    return foundWeekAtIndex;
};

export const getCurrentPhaseText = (workoutPhasesStartDates) => {
    let currentPhase = '';
    if (workoutPhasesStartDates) {
        let todayDate = new moment().format(DateFormatter.LINK_FORMAT);

        if (
            workoutPhasesStartDates.basePhase &&
            todayDate <= workoutPhasesStartDates.basePhase.basePhaseEnd &&
            todayDate >= workoutPhasesStartDates.basePhase.basePhaseStart
        ) {
            currentPhase = i18next.t(`training_plans:base_phase`);
        } else if (
            workoutPhasesStartDates.buildPhase &&
            todayDate <= workoutPhasesStartDates.buildPhase.buildPhaseEnd &&
            todayDate >= workoutPhasesStartDates.buildPhase.buildPhaseStart
        ) {
            currentPhase = i18next.t(`training_plans:build_phase`);
        } else if (
            workoutPhasesStartDates.peakPhase &&
            todayDate <= workoutPhasesStartDates.peakPhase.peakPhaseEnd &&
            todayDate >= workoutPhasesStartDates.peakPhase.peakPhaseStart
        ) {
            currentPhase = i18next.t(`training_plans:peak_phase`);
        }
    }
    return currentPhase;
};
